import { Box, Stack, Typography } from '@mui/material';
import {
  DataGridPremium,
  GridCallbackDetails,
  GridColDef,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import {
  columnsManagementSlotProps,
  DATA_GRID_ID,
  getDataGridLocalization,
  getInitialHiddenDataGridColumnsFromLocalStorage,
  getLLDate,
  MgPrisLocale,
  saveHiddenDataGridColumnsToLocalStorage,
} from '@retail/utils';
import { getTogglableDataGridColumns } from '@shared/utils';
import { StackedTextCell } from '@retail/components';
import { useAppTFunction } from '@retail/app/i18n';
import { useMemo } from 'react';
import { TotalFileDownloadSupplier } from '@retail/export/types';
import { useRowColorGrouping } from '@retail/hooks';
import dayjs from 'dayjs';

interface Props {
  language: string;
  suppliers: TotalFileDownloadSupplier[];
  isLoading?: boolean;
  selectedSupplierIds: number[];
  setSelectedSupplierIds: (supplierIds: number[]) => void;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 190,
  headerName: '',
  align: 'left',
  headerAlign: 'left',
};

export const ExportTable = ({
  language,
  suppliers,
  isLoading,
  selectedSupplierIds,
  setSelectedSupplierIds,
}: Props) => {
  const t = useAppTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const onRowsSelectionHandler = (
    selectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => {
    if (details.reason === 'singleRowSelection') {
      setSelectedSupplierIds(Array.from(selectionModel.ids) as number[]);
    } else if (selectedSupplierIds.length !== suppliers.length) {
      setSelectedSupplierIds(suppliers.map((supplier) => supplier.mgSupplierId));
    } else {
      setSelectedSupplierIds([]);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'mgSupplierName',
        width: 265,
        headerName: t('suppliers.columns.participantAccountName'),
      },
      {
        ...baseColumnProps,
        field: 'supplierId',
        width: 150,
        headerName: t('suppliers.columns.finfoAndMgSupplierId'),
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.mgSupplierId} bottomLine={row.finfoSupplierId} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'priceChangedStockItems',
        headerAlign: 'center',
        align: 'center',
        headerName: t('export.stockItemPrices'),
      },
      {
        ...baseColumnProps,
        field: 'priceChangedNonStockItems',
        headerAlign: 'center',
        align: 'center',
        headerName: t('export.nonStockItemPrices'),
      },
      {
        ...baseColumnProps,
        field: 'changedItems',
        headerAlign: 'center',
        align: 'center',
        headerName: t('export.articlesWithChange'),
      },
      {
        ...baseColumnProps,
        field: 'lastDownload',
        headerAlign: 'center',
        align: 'center',
        headerName: t('export.lastDownload'),
        renderCell: ({ row }) =>
          row.lastDownload ? (
            <StackedTextCell
              topLine={getLLDate(row.lastDownload)}
              bottomLine={dayjs(row.lastDownload).format('HH:mm:ss')}
            />
          ) : (
            '-'
          ),
      },
    ],
    [t]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium<TotalFileDownloadSupplier>
          columns={columns}
          rows={suppliers}
          checkboxSelection
          onRowSelectionModelChange={onRowsSelectionHandler}
          getRowId={(row) => row.mgSupplierId}
          getRowClassName={getRowColorForStripedTable}
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          rowHeight={65}
          disableRowSelectionOnClick
          disableColumnReorder
          disableColumnMenu
          hideFooter
          loading={isLoading}
          initialState={{
            columns: {
              columnVisibilityModel: {
                ...getInitialHiddenDataGridColumnsFromLocalStorage(
                  DATA_GRID_ID.EXPORT_TABLE,
                  getTogglableDataGridColumns(columns)
                ),
              },
            },
            pinnedColumns: { right: ['actions'] },
          }}
          onColumnVisibilityModelChange={(_, details) => {
            const toggableColumns = getTogglableDataGridColumns(columns);

            saveHiddenDataGridColumnsToLocalStorage(
              DATA_GRID_ID.EXPORT_TABLE,
              toggableColumns,
              details.api.state.columns.columnVisibilityModel
            );
          }}
          slots={{
            noRowsOverlay: () => (
              <Stack alignItems="center" justifyContent="center" gap={2} height="100%">
                <Typography variant="h3" color="grey.100">
                  {t('export.messages.noSuppliers')}
                </Typography>
              </Stack>
            ),
          }}
          slotProps={{
            ...columnsManagementSlotProps,
          }}
        />
      </Box>
    </Box>
  );
};
